function pickRandomWithIndex(items = []) {
  if (!items.length) {
    return {
      item: "",
      index: -1,
    };
  }

  const index = Math.floor(Math.random() * items.length);

  return {
    item: items[index],
    index,
  };
}

function fillTemplate(template, data = {}) {
  const pageLink =
    data.page ||
    data.reviewLink ||
    data.review_link ||
    data.demo ||
    "";

  return template
    .replaceAll("{{restaurant}}", data.restaurant || "your restaurant")
    .replaceAll("{{city}}", data.city || "your area")
    .replaceAll("{{page}}", pageLink)
    .replaceAll("{{review_link}}", pageLink)
    .replaceAll("{{demo}}", pageLink)
    .replaceAll("{{offer}}", data.offer || "your current promotion");
}

export const instagramTemplateGroups = [
  {
    id: "initial_contact",
    title: "Initial Contact",
    description:
      "First-contact messages for introducing TapToTable and sharing the restaurant's completed page.",
    templates: {
      made_for_you: {
        title: "Made for You",
        messages: [
          "Hey {{restaurant}} — I built a TapToTable Page specifically for {{restaurant}} after checking out your online presence. It shows how visitors could quickly find your promotion or event, menu, and important restaurant links.\n\nTake a look here: {{page}}",

          "Hi {{restaurant}}! I put together a TapToTable Page for {{restaurant}} using publicly available information from your restaurant. I thought it would be easier to show you the idea than try to explain it in a long message.\n\nHere’s your page: {{page}}",

          "Hey there! I built something for {{restaurant}} that I think you may find useful. It’s a focused TapToTable Page designed to help turn online interest into menu views, calls, website visits, and other customer actions.\n\nYou can review it here: {{page}}",

          "Hello {{restaurant}} team! I created a TapToTable Page for {{restaurant}} so you can see how your restaurant information and current promotion or event could be presented in one clean, mobile-friendly place.\n\nHere’s the page: {{page}}",

          "Hey {{restaurant}} — I went ahead and built a TapToTable Page for {{restaurant}}. There’s nothing you need to set up just to review it, and I’d be glad to make changes if anything needs adjusted.\n\nTake a look: {{page}}",

          "Hi {{restaurant}}! I came across your restaurant and created a TapToTable Page built around {{restaurant}}. It gives customers one simple place to view your current promotion or event and take the next step.\n\nHere it is: {{page}}",

          "Hey {{restaurant}}, I built a custom TapToTable Page for {{restaurant}} and wanted to send it directly to you. It’s designed for mobile visitors coming from places like Instagram.\n\nReview your page here: {{page}}",
        ],
      },

      friendly_intro: {
        title: "Friendly Introduction",
        messages: [
          "Hi {{restaurant}}! I came across your Instagram page and built a TapToTable Page for {{restaurant}}. I thought you might like to see how it looks with your restaurant information and customer actions in one place.\n\nHere’s the link: {{page}}",

          "Hello {{restaurant}} team! Hope you don’t mind me reaching out. I created a TapToTable Page for {{restaurant}} that could give your online visitors a simpler path to your menu, website, phone number, and current promotion or event.\n\nYou can see it here: {{page}}",

          "Hey {{restaurant}} — I was looking at restaurants in {{city}} and decided to build a TapToTable Page for {{restaurant}}. I’d love for you to take a look whenever you have a moment.\n\n{{page}}",

          "Hi there! I found {{restaurant}} online and put together a page showing how TapToTable could work for your restaurant. It’s already built, so you can review the actual page instead of just reading a sales pitch.\n\nHere it is: {{page}}",

          "Hey {{restaurant}}! I wanted to introduce myself and share something I built for {{restaurant}}. The page is designed to help restaurant visitors quickly find the information and actions that matter most.\n\nTake a look here: {{page}}",

          "Hello! I came across {{restaurant}} and thought it would be a strong fit for TapToTable, so I built the page first. Feel free to review it and let me know what you think.\n\n{{page}}",

          "Hi {{restaurant}}! I hope your day is going well. I put together a TapToTable Page for {{restaurant}} and wanted to make sure you had the opportunity to see it.\n\nHere’s your page: {{page}}",
        ],
      },

      quick_compliment: {
        title: "Restaurant Compliment",
        messages: [
          "Hey {{restaurant}} — your food and restaurant photos really stood out to me. I built a TapToTable Page for {{restaurant}} to show how that online attention could lead visitors toward your menu, promotion or event, and contact options.\n\n{{page}}",

          "Hi {{restaurant}}! Your restaurant has a strong online presence, and I thought {{restaurant}} would be a great fit for a focused TapToTable Page. I went ahead and built one for you.\n\nTake a look: {{page}}",

          "Hey there! I liked the look and personality of {{restaurant}}, so I created a TapToTable Page built around your restaurant. I’d be interested to hear what you think of it.\n\n{{page}}",

          "Hello {{restaurant}} team! Your restaurant looks like the kind of place people would be excited to discover online. I built a page for {{restaurant}} that helps turn that interest into clear customer actions.\n\nHere it is: {{page}}",

          "Hi {{restaurant}}! Your menu and food presentation caught my attention. I created a TapToTable Page for {{restaurant}} so online visitors can more easily take the next step.\n\nReview it here: {{page}}",

          "Hey {{restaurant}} — I really liked the vibe of your restaurant page. I built a TapToTable Page for {{restaurant}} that keeps the experience clean, focused, and easy to use on a phone.\n\n{{page}}",

          "Hi! {{restaurant}} looks like a restaurant with a lot to offer, so I created a page that puts your key information and customer options together in one place.\n\nHere’s the page I built for {{restaurant}}: {{page}}",
        ],
      },

      free_offer_angle: {
        title: "Promotion / Event Focus",
        messages: [
          "Hey {{restaurant}} — I built a TapToTable Page for {{restaurant}} showing how something timely like {{offer}} could be featured directly from your Instagram bio or other online profiles.\n\nTake a look here: {{page}}",

          "Hi {{restaurant}}! I created a focused TapToTable Page for {{restaurant}} so visitors could quickly see {{offer}} and then choose the next step, such as viewing your menu, calling, visiting your website, claiming a promotion, or responding to an event.\n\n{{page}}",

          "Hey there! I put together a TapToTable Page for {{restaurant}} centered around {{offer}}. It’s designed to make the featured promotion or event easy to understand and easy for customers to act on.\n\nHere’s the page: {{page}}",

          "Hello {{restaurant}} team! I built a page showing how {{restaurant}} could promote {{offer}} without making customers search through several different links.\n\nYou can review it here: {{page}}",

          "Hi {{restaurant}}! I thought {{offer}} could make a strong featured reason to visit, so I built a TapToTable Page around it for {{restaurant}}.\n\nTake a look: {{page}}",

          "Hey {{restaurant}} — instead of just suggesting a page, I went ahead and built a TapToTable Page for {{restaurant}} using {{offer}} as the featured promotion.\n\nHere it is: {{page}}",

          "Hi! I created a TapToTable Page for {{restaurant}} that gives {{offer}} a clear place to stand out while keeping your menu and restaurant links easy to reach.\n\n{{page}}",
        ],
      },

      results_focused: {
        title: "Results Focused",
        messages: [
          "Hey {{restaurant}} — I built a page for {{restaurant}} that could help turn more online visitors into menu views, phone calls, website visits, direction requests, and other customer actions.\n\nI thought it would be easier to show you: {{page}}",

          "Hi {{restaurant}}! A lot of restaurant visitors look at a page and leave without taking another step. I built a TapToTable Page for {{restaurant}} that gives them clear actions to choose from.\n\nTake a look: {{page}}",

          "Hey there! I created something for {{restaurant}} designed to move customers from simply viewing your restaurant online to actually checking the menu, calling, visiting, claiming a promotion, or responding to an event.\n\nHere it is: {{page}}",

          "Hello {{restaurant}} team! I built a TapToTable Page for {{restaurant}} with one main goal: help turn online interest into more restaurant visits and customer actions.\n\nReview it here: {{page}}",

          "Hi {{restaurant}}! I wanted to show you a practical way {{restaurant}} could get more value from Instagram and other online traffic, so I built the actual page for you.\n\n{{page}}",

          "Hey {{restaurant}} — your restaurant may already be getting profile views, but visitors still need a clear next step. I built a page for {{restaurant}} that gives them one.\n\nTake a look here: {{page}}",

          "Hi! I built a TapToTable Page for {{restaurant}} that brings your promotion or event, menu, contact options, and website together so visitors are more likely to take action.\n\n{{page}}",
        ],
      },

      owner_to_owner: {
        title: "Owner to Owner",
        messages: [
          "Hey {{restaurant}} — I’m building TapToTable to help restaurant owners get more value from the attention they already receive online. I built a page specifically for {{restaurant}} so you can see the idea for yourself.\n\n{{page}}",

          "Hi {{restaurant}}! I’m the owner of TapToTable, and I created a page for {{restaurant}} before reaching out. I’d rather let you review the actual result than send you a generic sales message.\n\nHere it is: {{page}}",

          "Hey there! I’m working directly with restaurants to help turn online interest into real customer actions. I built a TapToTable Page for {{restaurant}} and would appreciate your honest feedback.\n\n{{page}}",

          "Hello {{restaurant}} team! I created TapToTable as a practical tool for restaurant owners, and I went ahead and built a page for {{restaurant}} so you can see exactly what I mean.\n\nTake a look: {{page}}",

          "Hi {{restaurant}}! As a small business owner myself, I know generic pitches can be frustrating. That’s why I built the TapToTable Page for {{restaurant}} first and am simply sending you the result.\n\n{{page}}",

          "Hey {{restaurant}} — I’m personally helping restaurants create clearer paths from social media to menus, calls, websites, and visits. Here’s the page I built for {{restaurant}}:\n\n{{page}}",

          "Hi! I’m reaching out personally because I thought {{restaurant}} could benefit from TapToTable. I created the page first so you can decide based on something real.\n\n{{page}}",
        ],
      },

      curiosity: {
        title: "Curiosity",
        messages: [
          "Hey {{restaurant}} — I built something for {{restaurant}} today that I think you’ll want to see.\n\n{{page}}",

          "Hi {{restaurant}}! I came across your restaurant and created a custom page for {{restaurant}}. Thought I’d send it over rather than trying to explain it.\n\n{{page}}",

          "Hey there! Quick message—I made something for {{restaurant}} that could help your online visitors take the next step.\n\nHere it is: {{page}}",

          "Hello {{restaurant}} team! I put together a page specifically for {{restaurant}} and would be curious to hear your first impression.\n\n{{page}}",

          "Hi {{restaurant}}! I saw your restaurant online and had an idea, so I went ahead and built it for {{restaurant}}.\n\nTake a look: {{page}}",

          "Hey {{restaurant}} — this will make more sense when you see it. I built a TapToTable Page around your restaurant.\n\n{{page}}",

          "Hi! I created something for {{restaurant}} that gives customers a cleaner way to explore your restaurant and take action.\n\n{{page}}",
        ],
      },

      low_pressure: {
        title: "Low Pressure",
        messages: [
          "Hey {{restaurant}} — no pressure at all. I built a TapToTable Page for {{restaurant}} and thought you might want to see it whenever you have a moment.\n\n{{page}}",

          "Hi {{restaurant}}! I created this page for {{restaurant}} so you can review the idea before deciding whether it’s useful. There’s no obligation to activate it.\n\n{{page}}",

          "Hey there! I made a TapToTable Page for {{restaurant}} and wanted to pass it along. Feel free to take a look, and it’s completely fine if it isn’t a fit.\n\n{{page}}",

          "Hello {{restaurant}} team! I built this page for {{restaurant}} using public restaurant information. You’re welcome to review it with no pressure and no commitment.\n\n{{page}}",

          "Hi {{restaurant}}! This isn’t a request for you to sign up before seeing anything. I already built the TapToTable Page for {{restaurant}}, and you can decide what you think after reviewing it.\n\n{{page}}",

          "Hey {{restaurant}} — I wanted to share the page I created for your restaurant. No sales pressure; I’d simply appreciate you taking a look when convenient.\n\n{{page}}",

          "Hi! I built a TapToTable Page for {{restaurant}} and am sending it over for you to review. If it’s useful, great. If not, no worries at all.\n\n{{page}}",
        ],
      },

      social_proof: {
        title: "Customer Action",
        messages: [
          "Hey {{restaurant}} — a focused restaurant page can help turn profile visits into menu clicks, calls, website visits, direction requests, promotion claims, event responses, and other customer actions. I built one specifically for {{restaurant}}.\n\n{{page}}",

          "Hi {{restaurant}}! Customers are more likely to take action when the next step is clear. I created a TapToTable Page for {{restaurant}} that brings those actions together in one place.\n\nTake a look: {{page}}",

          "Hey there! Instead of sending visitors through a cluttered list of unrelated links, {{restaurant}} could direct them to one focused customer page. I built it for you here:\n\n{{page}}",

          "Hello {{restaurant}} team! I created a TapToTable Page for {{restaurant}} that gives online visitors easy access to your promotion or event, menu, website, phone number, and other important actions.\n\n{{page}}",

          "Hi {{restaurant}}! A clean mobile page can make it easier for interested visitors to become real customers. Here’s the TapToTable Page I built for {{restaurant}}:\n\n{{page}}",

          "Hey {{restaurant}} — I built a page designed to make the journey from discovering {{restaurant}} online to taking action much simpler.\n\nReview it here: {{page}}",

          "Hi! I created a TapToTable Page for {{restaurant}} that helps keep customer attention focused on your restaurant instead of sending visitors through a confusing set of links.\n\n{{page}}",
        ],
      },

      limited_time: {
        title: "Ready for Review",
        messages: [
          "Hey {{restaurant}} — the TapToTable Page I built for {{restaurant}} is ready for you to review. If you like the direction, I can make adjustments before you decide whether to activate it.\n\n{{page}}",

          "Hi {{restaurant}}! Your TapToTable Page is ready. Take a look, and let me know if you’d like anything changed for {{restaurant}}.\n\n{{page}}",

          "Hey there! I finished building the TapToTable Page for {{restaurant}}. You can review the page first, request changes, and only activate it if you’re happy with it.\n\n{{page}}",

          "Hello {{restaurant}} team! I put together your TapToTable Page and wanted to send the review link directly to you. I’m happy to update the restaurant details or featured promotion or event if needed.\n\n{{page}}",

          "Hi {{restaurant}}! The page I built for {{restaurant}} is available for review. Nothing needs to be activated just to see it.\n\nTake a look here: {{page}}",

          "Hey {{restaurant}} — your page is built and ready to review. If something doesn’t look right, send me the changes and I can adjust it.\n\n{{page}}",

          "Hi! I finished the TapToTable Page for {{restaurant}}. Review it when you have a chance, and let me know what you think.\n\n{{page}}",
        ],
      },
    },
  },

  {
    id: "follow_up",
    title: "Follow-Up Sequence",
    description:
      "Messages for continuing the same conversation after the initial TapToTable Page has been sent.",
    templates: {
      follow_up_1: {
        title: "Follow-Up 1",
        suggestedTiming: "2–3 days after initial contact",
        messages: [
          "Hey {{restaurant}} — just following up in case my earlier message got buried. I built a TapToTable Page for {{restaurant}} and wanted to make sure you received the link.\n\nHere it is again: {{page}}",

          "Hi {{restaurant}}! Just checking back to see whether you had a chance to look at the page I created for {{restaurant}}.\n\n{{page}}",

          "Hey there! I wanted to quickly follow up on the TapToTable Page I sent for {{restaurant}}. I’d be interested to hear your first impression whenever you have time.\n\n{{page}}",

          "Hello {{restaurant}} team! Just bumping this in case the original message was missed. Here’s the page I built for {{restaurant}}:\n\n{{page}}",

          "Hi {{restaurant}}! I know restaurant schedules get busy, so I wanted to resend your TapToTable Page in case you haven’t had a chance to review it yet.\n\n{{page}}",

          "Hey {{restaurant}} — quick follow-up on the page I created for your restaurant. No rush, but I wanted to make sure the link reached you.\n\n{{page}}",

          "Hi! Just checking in about the TapToTable Page for {{restaurant}}. I can also make changes if there’s anything you’d prefer to feature differently.\n\n{{page}}",
        ],
      },

      follow_up_2: {
        title: "Follow-Up 2",
        suggestedTiming: "4–5 days after Follow-Up 1",
        messages: [
          "Hey {{restaurant}} — I wanted to check in one more time about the TapToTable Page I built for {{restaurant}}. Do you think something like this could be useful for your restaurant?\n\n{{page}}",

          "Hi {{restaurant}}! Just following up again in case you had questions about the page. You can review it first, request changes, and only activate it if you decide it’s a good fit.\n\n{{page}}",

          "Hey there! I know things get busy, so I’m sending the TapToTable Page for {{restaurant}} again. I’d be happy to adjust the promotion, event, or restaurant information if needed.\n\n{{page}}",

          "Hello {{restaurant}} team! I wanted to make sure you had another opportunity to see the page I created for {{restaurant}} before I close out my follow-up.\n\n{{page}}",

          "Hi {{restaurant}}! Have you had a chance to review the TapToTable Page for {{restaurant}}? Even a quick yes, no, or not right now would be helpful.\n\n{{page}}",

          "Hey {{restaurant}} — I’m checking back about your page. The goal is to help turn more online interest into menu views, calls, visits, and other customer actions.\n\nHere it is again: {{page}}",

          "Hi! I wanted to follow up once more about the TapToTable Page for {{restaurant}}. Let me know if you’d like me to make any changes before you review it again.\n\n{{page}}",
        ],
      },

      final_follow_up: {
        title: "Final Follow-Up",
        suggestedTiming: "About one week after Follow-Up 2",
        messages: [
          "Hey {{restaurant}} — this will be my final follow-up, and I won’t keep filling your inbox. I just wanted to send the TapToTable Page for {{restaurant}} one last time in case it’s useful later.\n\n{{page}}",

          "Hi {{restaurant}}! I know you’re busy, so I’ll close this out after today. Here’s the page I built for {{restaurant}} in case you’d still like to review it.\n\n{{page}}",

          "Hey there! Last message from me about the TapToTable Page for {{restaurant}}. There’s no pressure—I simply wanted to make sure you had the link before I stopped following up.\n\n{{page}}",

          "Hello {{restaurant}} team! I’ll leave you alone after this one. I built the page specifically for {{restaurant}}, so I wanted to send it one final time before closing the conversation.\n\n{{page}}",

          "Hi {{restaurant}}! This is my last check-in. If TapToTable isn’t something you need right now, no problem at all. You can still view the page I built for {{restaurant}} here:\n\n{{page}}",

          "Hey {{restaurant}} — I’m wrapping up my follow-up, but I wanted to leave you with the page link in case the timing is better later.\n\n{{page}}",

          "Hi! I won’t send any more follow-ups after this. Thank you for your time, and here’s the TapToTable Page for {{restaurant}} one last time:\n\n{{page}}",
        ],
      },
    },
  },
];

function flattenInstagramTemplates() {
  const templates = {};

  instagramTemplateGroups.forEach((group) => {
    Object.entries(group.templates).forEach(([type, configuration]) => {
      templates[type] = configuration.messages;
    });
  });

  return templates;
}

function buildInstagramTemplateMeta() {
  const metadata = {};

  instagramTemplateGroups.forEach((group) => {
    Object.entries(group.templates).forEach(([type, configuration]) => {
      metadata[type] = {
        type,
        title: configuration.title,
        groupId: group.id,
        groupTitle: group.title,
        description: group.description,
        suggestedTiming: configuration.suggestedTiming || null,
        variationCount: configuration.messages.length,
      };
    });
  });

  return metadata;
}

/*
 * Kept as a flat object for compatibility with existing components that use:
 *
 * instagramTemplates[type]
 *
 * New interfaces can use instagramTemplateGroups to display organized
 * sections such as Initial Contact and Follow-Up Sequence.
 */
export const instagramTemplates = flattenInstagramTemplates();

export const instagramTemplateMeta = buildInstagramTemplateMeta();

/*
 * Returns the message plus information identifying exactly which template
 * was selected. These values can later be stored on a lead or outreach log:
 *
 * {
 *   type: "made_for_you",
 *   templateTitle: "Made for You",
 *   groupId: "initial_contact",
 *   groupTitle: "Initial Contact",
 *   templateIndex: 0,
 *   variationNumber: 1,
 *   message: "..."
 * }
 */
export function buildInstagramDMSelection(data = {}) {
  const requestedType = data.type || "made_for_you";
  const type = instagramTemplates[requestedType]
    ? requestedType
    : "made_for_you";

  const templates = instagramTemplates[type];
  const selection = pickRandomWithIndex(templates);
  const metadata = instagramTemplateMeta[type];

  return {
    type,
    templateTitle: metadata.title,
    groupId: metadata.groupId,
    groupTitle: metadata.groupTitle,
    suggestedTiming: metadata.suggestedTiming,
    templateIndex: selection.index,
    variationNumber:
      selection.index >= 0 ? selection.index + 1 : null,
    message: fillTemplate(selection.item, data),
  };
}

/*
 * Preserves the current behavior for existing components.
 * It continues returning only the completed message string.
 */
export function buildInstagramDM(data = {}) {
  return buildInstagramDMSelection(data).message;
}
